import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { errorToast, successToast } from 'utils/helpers/notify';
import { errorHandler } from 'utils/helpers/errorHandler';
import { useObject } from 'utils/api/useObject';
import { useEditObjectMutation } from 'pages/facility/api/mutations/generated/EditObject';

export const useFacilityPenalty = () => {
  const { object, id } = useObject();
  const [editObject, { loading }] = useEditObjectMutation();

  const settings = object?.settings;

  const initialValues = useMemo(
    () => ({
      penaltySwitch: settings?.isCancelShiftHoldEnabled || false,
      penaltyTime: settings?.cancelShiftHoldThresholdHours
        ? dayjs().set('hour', settings.cancelShiftHoldThresholdHours)
        : null,
      penaltyCost: settings?.cancelShiftHoldAmount ? String(settings.cancelShiftHoldAmount) : '',
    }),
    [settings],
  );

  const validate = (values: typeof initialValues) => {
    const error: any = {};
    if (Number(values.penaltyCost) > 100000 || Number(values.penaltyCost) < 1) {
      error.penaltyCost = 'Допустимый размер удержания от 1 до 100.000 рублей';
    }
    if (!values.penaltyTime) {
      error.penaltyTime = 'Обязательное поле';
    }
    return error;
  };

  const onSubmit = useCallback(
    (values: typeof initialValues) => {
      if (!id) return;
      editObject({
        variables: {
          input: {
            objectId: id,
            settings: {
              isCancelShiftHoldEnabled: values.penaltySwitch,
              cancelShiftHoldThresholdHours: dayjs(values.penaltyTime).get('hours'),
              cancelShiftHoldAmount: Number(values.penaltyCost),
            },
          },
        },
      })
        .then(() => {
          successToast('Удержание обновлено');
        })
        .catch(e => {
          errorToast(errorHandler(e));
        });
    },
    [editObject, id],
  );

  const removePenalty = useCallback(() => {
    if (!id) return;
    editObject({
      variables: {
        input: {
          objectId: id,
          settings: {
            isCancelShiftHoldEnabled: false,
            cancelShiftHoldThresholdHours: 0,
            cancelShiftHoldAmount: 0,
          },
        },
      },
    })
      .then(() => {
        successToast('Удержание удалено');
      })
      .catch(() => {
        errorToast('Произошла ошибка при удалении удержания');
      });
  }, [id, editObject]);

  return { initialValues, validate, onSubmit, removePenalty, loading };
};
